// packages/web-ui/src/state/keymap.ts
//
// W2-S10 — single source-of-truth keymap registry for the **Master Keyboard
// Cheat-Sheet** overlay (UX §17). Future stories (W3..W6) append entries to
// this list rather than re-declaring per-panel cheat sheets.
//
// Each binding is purely descriptive — the actual key listeners live in the
// panels that own those affordances (e.g. tab keybindings in
// `useTabKeybindings`, destination switches in `useDestinationKeybindings`).
// The cheat-sheet renders this registry so users see the canonical list.
//
// AC #3 of story 2-10: lists every binding from UX §6.1.9 (chat),
// §6.2.6 (composer), §6.3.6 (file viewer), §6.4.5 (diff), §6.5.4 (KB browser),
// §6.6.6 (view renderer), §7.8 (utility drawer), §8.5 (ContextPanel),
// §9.6 (sessions), plus AppShell global bindings.

/** A single keyboard binding entry. */
export interface KeymapBinding {
  /** Display string, e.g. "Ctrl+B" or "?". Pretty-printed. */
  keys: string;
  /** One-line human-readable description. */
  description: string;
  /**
   * Optional UX-spec back-reference (e.g. "UX §6.1.9"). Surfaced in the
   * cheat-sheet so future contributors can trace the source.
   */
  ref?: string;
}

/** A grouped section of bindings (one per panel/scope). */
export interface KeymapGroup {
  /** Section title rendered in the cheat-sheet (e.g. "Chat"). */
  title: string;
  /** Stable id for testing / deep-linking. */
  id: string;
  /** Bindings in display order. */
  bindings: KeymapBinding[];
}

export const KEYMAP_GROUPS: ReadonlyArray<KeymapGroup> = [
  {
    id: 'global',
    title: 'Global (AppShell)',
    bindings: [
      { keys: 'Ctrl+B', description: 'Focus the Activity Bar', ref: 'UX §17 / W2-S2' },
      { keys: 'Ctrl+\\', description: 'Toggle the Context Panel', ref: 'UX §17' },
      { keys: 'Ctrl+1..9', description: 'Switch to tab N', ref: 'UX §17 / W2-S4' },
      { keys: '?', description: 'Open this keyboard cheat-sheet', ref: 'UX §17' },
      { keys: 'Ctrl+/', description: 'Open this keyboard cheat-sheet', ref: 'UX §17' },
      { keys: 'Esc', description: 'Close any open overlay or modal', ref: 'UX §17' },
    ],
  },
  {
    id: 'chat',
    title: 'Chat',
    bindings: [
      { keys: 'Enter', description: 'Send the message', ref: 'UX §6.1.9' },
      { keys: 'Shift+Enter', description: 'Insert newline in composer', ref: 'UX §6.1.9' },
      { keys: 'Ctrl+R', description: 'Regenerate last assistant turn', ref: 'UX §6.1.9' },
      { keys: 'Up', description: 'Recall previous user message', ref: 'UX §6.1.9' },
      { keys: 'Down', description: 'Recall next user message', ref: 'UX §6.1.9' },
    ],
  },
  {
    id: 'composer',
    title: 'Composer',
    bindings: [
      { keys: '@', description: 'Open mention autocomplete', ref: 'UX §6.2.6' },
      { keys: '/', description: 'Open slash-command palette', ref: 'UX §6.2.6' },
      { keys: 'Esc', description: 'Close mention/slash popup', ref: 'UX §6.2.6' },
      { keys: 'Tab', description: 'Accept highlighted suggestion', ref: 'UX §6.2.6' },
      { keys: 'Up / Down', description: 'Move suggestion focus', ref: 'UX §6.2.6' },
    ],
  },
  {
    id: 'file-viewer',
    title: 'File Viewer',
    bindings: [
      { keys: 'Ctrl+S', description: 'Save the current buffer', ref: 'UX §6.3.6' },
      { keys: 'Ctrl+F', description: 'Open in-file find', ref: 'UX §6.3.6' },
      { keys: 'Ctrl+G', description: 'Find next', ref: 'UX §6.3.6' },
      { keys: 'Ctrl+Shift+G', description: 'Find previous', ref: 'UX §6.3.6' },
      { keys: 'Esc', description: 'Close find', ref: 'UX §6.3.6' },
    ],
  },
  {
    id: 'diff',
    title: 'Diff Viewer',
    bindings: [
      { keys: 'J', description: 'Next change hunk', ref: 'UX §6.4.5' },
      { keys: 'K', description: 'Previous change hunk', ref: 'UX §6.4.5' },
      { keys: 'A', description: 'Accept hunk', ref: 'UX §6.4.5' },
      { keys: 'X', description: 'Reject hunk', ref: 'UX §6.4.5' },
      { keys: 'Enter', description: 'Toggle expand/collapse hunk', ref: 'UX §6.4.5' },
    ],
  },
  {
    id: 'kb-browser',
    title: 'KB Browser',
    bindings: [
      { keys: 'Up / Down', description: 'Move row focus', ref: 'UX §6.5.4' },
      { keys: 'Right', description: 'Expand row', ref: 'UX §6.5.4' },
      { keys: 'Left', description: 'Collapse row', ref: 'UX §6.5.4' },
      { keys: 'Enter', description: 'Open the entity in a tab', ref: 'UX §6.5.4' },
      { keys: '/', description: 'Focus the KB search input', ref: 'UX §6.5.4' },
    ],
  },
  {
    id: 'view-renderer',
    title: 'View Renderer',
    bindings: [
      { keys: '+ / -', description: 'Zoom in / out', ref: 'UX §6.6.6' },
      { keys: '0', description: 'Reset zoom to fit', ref: 'UX §6.6.6' },
      { keys: 'Space (drag)', description: 'Pan the canvas', ref: 'UX §6.6.6' },
      { keys: 'Ctrl+E', description: 'Export the current view as SVG', ref: 'UX §6.6.6' },
    ],
  },
  {
    id: 'utility-drawer',
    title: 'Utility Drawer',
    bindings: [
      { keys: 'Ctrl+J', description: 'Toggle the utility drawer', ref: 'UX §7.8' },
      { keys: 'Ctrl+Shift+J', description: 'Toggle Debug-tab override (session)', ref: 'UX §7.6' },
      { keys: '1 / 2 / 3', description: 'Switch drawer tab (Activity / Detail / Debug)', ref: 'UX §7.8' },
    ],
  },
  {
    id: 'context-panel',
    title: 'Context Panel',
    bindings: [
      { keys: 'Ctrl+\\', description: 'Toggle context panel', ref: 'UX §8.5' },
      { keys: 'F6', description: 'Cycle focus between panels', ref: 'UX §8.5' },
    ],
  },
  {
    id: 'sessions',
    title: 'Sessions',
    bindings: [
      { keys: 'Ctrl+N', description: 'New chat session', ref: 'UX §9.6' },
      { keys: 'Up / Down', description: 'Move session focus', ref: 'UX §9.6' },
      { keys: 'Enter', description: 'Activate the focused session', ref: 'UX §9.6' },
      { keys: 'Delete', description: 'Open the delete-confirmation dialog', ref: 'UX §9.6' },
      { keys: 'Esc', description: 'Cancel the delete-confirmation dialog', ref: 'UX §9.6' },
    ],
  },
] as const;

/** Pure utility — flatten the registry into a `keys → group/binding` lookup map. */
export function flattenKeymap(): Array<{
  groupId: string;
  groupTitle: string;
  binding: KeymapBinding;
}> {
  return KEYMAP_GROUPS.flatMap((g) =>
    g.bindings.map((binding) => ({
      groupId: g.id,
      groupTitle: g.title,
      binding,
    })),
  );
}

/**
 * Detect whether the current focused element is an editable input where
 * `?` should NOT trigger the cheat-sheet (AC #3 — suppress when input
 * focused). Pure helper so consumers and tests can share the same logic.
 */
export function isEditableElementFocused(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') return true;
  // `isContentEditable` is the live DOM accessor (true when inherited from
  // an ancestor); we also explicitly check the attribute string so jsdom
  // (which doesn't compute the inheritance) treats `contenteditable="true"`
  // as editable too.
  if (target.isContentEditable) return true;
  const ce = target.getAttribute('contenteditable');
  if (ce !== null && ce !== 'false') return true;
  // base-ui combo boxes / menus also use role="textbox"
  if (target.getAttribute('role') === 'textbox') return true;
  return false;
}

/**
 * Detect the canonical "open cheat-sheet" key combination (AC #3):
 *   - Bare `?` (Shift+/ on US layouts surfaces as `key === '?'`) without modifier
 *   - `Ctrl+/`
 *
 * Returns `true` when the event matches and the cheat-sheet should open.
 */
export function isCheatSheetTrigger(event: KeyboardEvent): boolean {
  // Ctrl+/ — works regardless of focus context when the listener fires.
  if (event.key === '/' && (event.ctrlKey || event.metaKey)) return true;
  // Bare "?" — Shift can be true (typing ? on a US layout requires Shift),
  // but Ctrl/Alt/Meta must NOT be down or it's a different chord.
  if (event.key === '?' && !event.ctrlKey && !event.altKey && !event.metaKey) return true;
  return false;
}
